import { computed } from 'vue';

import { useEstimatedSegments } from './useEstimatedSegments';
import { useCurrentCalculation } from './useCurrentCalculation';

type FinancingPdfJson = { [key: string]: string | number };

const FINANCING_PDF_JSON: FinancingPdfJson = {
  calculationName: '',
  pricingNetPrice: '',
  pricingGrossPrice: '',
  pricingSubsidy: '',
  pricingClientContribution: '',
  pricingThermalDeduction: '',

  paymentVat: '',
  paymentFirstSubsidy: '',
  paymentSecondSubsidy: '',

  groupLoanAmount: '',
  groupInterestRate: '',
  groupInstallmentCount: '',

  financingExternalInstallment: '',
  financingTrifyInstallment: '',

  finalPrice: '',
};

function formatRuleValue(rule: any | null | undefined): string {
  if (!rule) return '-';
  const value = rule.eval ?? '-';
  const unit = rule.unit || '';
  if (!unit) return String(value);
  if (unit === '%') return `${value}${unit}`;
  return `${value} ${unit}`;
}

export function useCalculationFinancingPdfData() {
  const { financingSegments } = useEstimatedSegments({
    visibility: 'visible-only',
    hidden: 'unhidden-only',
    ruleTypes: ['calculation'],
    includeChildRules: false,
    includeSegmentRules: true,
    includeGroupRules: true,
  });
  const { currentCalculation } = useCurrentCalculation();

  const financingSegment = computed(() => financingSegments.value?.[0]);

  function fillFinancingPdfJson(): FinancingPdfJson {
    const pdfJson: FinancingPdfJson = { ...FINANCING_PDF_JSON };

    const segment = financingSegment.value;
    if (!segment) {
      pdfJson.calculationName = currentCalculation.value?.name || '-';
      return pdfJson;
    }

    const rules = segment.rules || [];
    const findRuleBySlug = (slug: string) => rules.find((r: any) => r.slug === slug);

    const netPrice = findRuleBySlug('cena-netto-dla-klienta-wszystkie-technologie');
    const grossPrice = findRuleBySlug('cena-brutto-dla-klienta-wszystkie-technologie');
    const subsidy = findRuleBySlug('dotacja');
    const clientContribution = findRuleBySlug('kwota-po-dotacji-wklad-wlasny-klienta');
    const thermalDeduction = findRuleBySlug('ulga-termomodernizacyjna-do-odliczenia');

    pdfJson.pricingNetPrice = formatRuleValue(netPrice);
    pdfJson.pricingGrossPrice = formatRuleValue(grossPrice);
    pdfJson.pricingSubsidy = formatRuleValue(subsidy);
    pdfJson.pricingClientContribution = formatRuleValue(clientContribution);
    pdfJson.pricingThermalDeduction = formatRuleValue(thermalDeduction);

    const vatPayment = findRuleBySlug('platnosc-1-vat-od-pierwszej-transzy-dotacji-klient');
    const firstSubsidy = findRuleBySlug('platnosc-2-pierwsza-transza-dotacji-35percent-wfos');
    const secondSubsidy = findRuleBySlug(
      'platnosc-3-druga-transza-dotacji-wfos-po-montazu-i-oplaceniu-faktury-koncowej'
    );

    pdfJson.paymentVat = formatRuleValue(vatPayment);
    pdfJson.paymentFirstSubsidy = formatRuleValue(firstSubsidy);
    pdfJson.paymentSecondSubsidy = formatRuleValue(secondSubsidy);

    const externalFinancing = findRuleBySlug('wariant-2-rata-finansowanie-zewnetrzne');
    const trifyInstallment = findRuleBySlug('wariant-2-rata-trify-odsetkowa');

    pdfJson.financingExternalInstallment = formatRuleValue(externalFinancing);
    pdfJson.financingTrifyInstallment = formatRuleValue(trifyInstallment);

    const allGroupRules = (segment.groups || []).flatMap((g: any) => g.rules || []);
    const findGroupRuleBySlug = (slug: string) => allGroupRules.find((r: any) => r.slug === slug);

    const loanAmount = findGroupRuleBySlug('kwota-kredytu-netto-do-wyplaty');
    const installmentCount = findGroupRuleBySlug('liczba-rat');
    const interestRate = findGroupRuleBySlug('oprocentowanie-nominalne-w-skali-roku');

    pdfJson.groupLoanAmount = formatRuleValue(loanAmount);
    pdfJson.groupInstallmentCount = formatRuleValue(installmentCount);
    pdfJson.groupInterestRate = formatRuleValue(interestRate);

    const finalRule = loanAmount?.eval ? loanAmount : clientContribution;
    pdfJson.finalPrice = formatRuleValue(finalRule);

    pdfJson.calculationName = currentCalculation.value?.name || '-';

    return pdfJson;
  }

  return { fillFinancingPdfJson };
}
